import { CssBaseline } from '@mui/material';
import { StyledEngineProvider, ThemeProvider } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import PageLoader from 'components/common/PageLoader';
import theme from 'components/common/Theme/theme';
import React, { Suspense, useEffect } from 'react';
import { Provider, useDispatch, useSelector } from 'react-redux';
import { BrowserRouter as Router } from 'react-router-dom';
import { getSelf } from 'store/auth/actions';
import { selectAccessToken, selectUser } from 'store/auth/selectors';
import AnalyticsWrapper from './AnalyticsWrapper';
import Document from './Document';
import Routes from './router/routes';
import { store } from './store/createStore';

/**
 * Fetches /self on app load when a token exists but user data hasn't been loaded yet.
 * This covers the page-refresh case where the token is restored from cookies/localStorage.
 */
const AuthInitializer = ({ children }: { children: React.ReactElement }) => {
  const dispatch = useDispatch();
  const accessToken = useSelector(selectAccessToken);
  const user = useSelector(selectUser);

  useEffect(() => {
    if (accessToken && !user) {
      dispatch(getSelf());
    }
  }, [accessToken, user, dispatch]);

  return children;
};

const App = () => (
  <Provider store={store}>
    <Router>
      <Document>
        <StyledEngineProvider injectFirst>
          <ThemeProvider theme={theme}>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <CssBaseline />
              <AuthInitializer>
                <AnalyticsWrapper>
                  <Suspense fallback={<PageLoader />}>
                    <Routes />
                  </Suspense>
                </AnalyticsWrapper>
              </AuthInitializer>
            </LocalizationProvider>
          </ThemeProvider>
        </StyledEngineProvider>
      </Document>
    </Router>
  </Provider>
);
export default App;
